"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useFeatureValue } from "@growthbook/growthbook-react";
import { LifeBuoy, PhoneCall, X } from "lucide-react";
import { useAuth } from "@/hooks/use-auth";
import { useReducedMotion } from "@/hooks/use-reduced-motion";
import { useBookingCta, useBookingImpression } from "@/features/booking/use-booking-cta";
import { useConversionSurfaceState } from "@/lib/powerspot/use-conversion-surface-state";
import { safeLocalStorage } from "@/lib/storage/safe-local-storage";
import { trackFunnel } from "@/lib/integrations/sentry-tracking";
import { pushDataLayer } from "@/lib/integrations/ga4";
import { cn } from "@/lib/utils/cn";
import { launchBarCopy, type LaunchBarSegment } from "@/features/launch/launch-bar-copy";
import type { BookingSurface } from "@/lib/utils/calendly";

const DISMISS_KEY_PREFIX = "speak_launch_bar_dismissed_";
/**
 * Dismissing hides the bar until the next day, not for a week. During a launch the bar is
 * the only thing surfacing the capability, so a stray click should cost one day of reach,
 * not seven. Acting on the CTA counts as a dismissal too — someone who just booked does
 * not need to be asked again today.
 */
const DISMISS_TTL_MS = 24 * 60 * 60 * 1000;
const ROTATE_MS = 4500;

function isStoredDismissed(userId: string): boolean {
  const raw = safeLocalStorage()?.getItem(`${DISMISS_KEY_PREFIX}${userId}`);
  if (!raw) return false;
  const at = Number(raw);
  return Number.isFinite(at) && Date.now() - at < DISMISS_TTL_MS;
}

function storeDismissed(userId: string): void {
  safeLocalStorage()?.setItem(`${DISMISS_KEY_PREFIX}${userId}`, String(Date.now()));
}

/**
 * Sitewide launch bar for multimodal (audio + video) analysis, rendered above
 * the topbar.
 *
 * Two audiences, one bar:
 *  - `isMultimodalAnalysis === true` — an operator already switched the feature
 *    on for their company, but the only control is a toggle inside a
 *    single-file chat, so nothing surfaces it. This segment gets a pointer to
 *    the control and no booking ask.
 *  - everyone else — the feature is invisible to them and a call is the only
 *    way to get it, so this segment gets the unified booking CTA. Gated on
 *    salesQualified, matching every other in-app booking surface.
 *
 * Suppressed whenever the dashboard hero overlay owns the screen (expired
 * trial, failed payment): a launch pitch on top of a billing failure is noise.
 */
export function LaunchAnnouncementBar() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const barRef = useRef<HTMLDivElement>(null);
  const reducedMotion = useReducedMotion();
  const flagOn = useFeatureValue("multimodal-launch-bar", false);
  const showIncentive = useFeatureValue("multimodal-launch-bar-incentive", false);
  const segment: LaunchBarSegment = user?.isMultimodalAnalysis === true ? "enabled" : "prospect";
  // Enabled workspaces book for setup help, not to buy, so their bookings land on their own surface.
  const surface: BookingSurface = segment === "enabled" ? "launch_bar_support" : "launch_bar_a";
  const cta = useBookingCta(surface);
  const { heroOverlay } = useConversionSurfaceState();

  const [dismissedNow, setDismissedNow] = useState(false);
  const [paused, setPaused] = useState(false);
  const [index, setIndex] = useState(0);

  const shown = Boolean(
    flagOn
      && user
      && !heroOverlay
      && !dismissedNow
      && !isStoredDismissed(user._id)
      && (segment === "enabled" || cta.qualified),
  );

  useBookingImpression(cta, surface, shown);

  // Fires for BOTH segments. It was gated on segment === "enabled", so a week of
  // prospect impressions recorded 26 events (internal workspaces only) while the
  // bar was rendering to everyone — the bar's real reach and conversion could not
  // be measured at all. `segment` is a dimension on the event, never a filter.
  const impressionFired = useRef(false);
  useEffect(() => {
    if (!shown || impressionFired.current) return;
    impressionFired.current = true;
    trackFunnel("launch", "bar_shown", { segment });
    pushDataLayer("Launch", "Bar Shown", `launch_bar_${segment}`, undefined, { segment });
  }, [shown, segment]);

  const copy = useMemo(
    () =>
      launchBarCopy({
        segment,
        goal: user?.onboarding?.goal,
        isPaidActive: cta.isPaidActive,
        showIncentive,
      }),
    [segment, user?.onboarding?.goal, cta.isPaidActive, showIncentive],
  );
  const lines = useMemo(() => [copy.primary, ...copy.fragments], [copy]);

  useEffect(() => {
    if (reducedMotion || paused || !shown || lines.length < 2) return;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % lines.length), ROTATE_MS);
    return () => window.clearInterval(id);
  }, [reducedMotion, paused, shown, lines.length]);

  const hideUntilTomorrow = () => {
    storeDismissed(user!._id);
    setDismissedNow(true);
  };

  // Full-bleed pages (media insight, media editor) size themselves off 100dvh minus
  // --topbar-height and know nothing about this bar. Publish its rendered height here so
  // those pages can subtract it too, or their fixed-height column overflows the real
  // viewport and pushes content like the sticky player bar below the fold.
  useEffect(() => {
    const root = document.documentElement;
    if (!shown) {
      root.style.setProperty("--launch-bar-height", "0px");
      return;
    }
    const el = barRef.current;
    if (!el) return;
    const setHeight = () => root.style.setProperty("--launch-bar-height", `${el.offsetHeight}px`);
    setHeight();
    const observer = new ResizeObserver(setHeight);
    observer.observe(el);
    return () => {
      observer.disconnect();
      root.style.setProperty("--launch-bar-height", "0px");
    };
  }, [shown]);

  if (!shown || !user) return null;

  const line = reducedMotion ? lines[0] : (lines[index] ?? lines[0]);
  const isProspect = segment === "prospect";

  return (
    <div
      ref={barRef}
      // Message + CTA sit centered as one group (matching the website bar);
      // only the dismiss control anchors to the edge.
      className="relative flex shrink-0 items-center justify-center gap-3 bg-foreground px-12 py-2 text-background"
      data-testid="launch-announcement-bar"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
    >
      <span className="hidden shrink-0 rounded-full border border-background/45 px-2 py-0.5 text-[11px] font-bold uppercase tracking-wider sm:inline-flex">
        {t("LAUNCH_BAR.BADGE", "New")}
      </span>

      {/* The rotation is decorative: screen readers get the full message once, below, instead of
          a live region that re-announces every few seconds. */}
      <div className="min-w-0 overflow-hidden" aria-hidden="true">
        <span
          key={reducedMotion ? "static" : index}
          className={cn("block truncate text-sm", !reducedMotion && "animate-in fade-in duration-500")}
        >
          {t(line.key, line.text)}
        </span>
      </div>
      <p className="sr-only">{lines.map((l) => t(l.key, l.text)).join(" ")}</p>

      {isProspect ? (
        <a
          href={cta.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => { cta.trackClick(); hideUntilTomorrow(); }}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-background px-3 py-1 text-sm font-medium text-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background focus-visible:ring-offset-2 focus-visible:ring-offset-foreground"
          data-testid="launch-announcement-bar-cta"
        >
          <PhoneCall className="h-3.5 w-3.5" aria-hidden="true" />
          {t(cta.label.key, cta.label.text)}
        </a>
      ) : (
        <a
          href={cta.href}
          target="_blank"
          rel="noopener noreferrer"
          onClick={() => {
            cta.trackClick();
            trackFunnel("launch", "bar_clicked", { segment });
            pushDataLayer("Launch", "Bar Clicked", "launch_bar_support", undefined, { segment });
            hideUntilTomorrow();
          }}
          className="inline-flex shrink-0 items-center gap-1.5 rounded-md bg-background px-3 py-1 text-sm font-medium text-foreground hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background focus-visible:ring-offset-2 focus-visible:ring-offset-foreground"
          data-testid="launch-announcement-bar-support"
        >
          <LifeBuoy className="h-3.5 w-3.5" aria-hidden="true" />
          {t("LAUNCH_BAR.GET_HELP", "Get setup help")}
        </a>
      )}

      <button
        type="button"
        onClick={hideUntilTomorrow}
        className="absolute right-3 top-1/2 shrink-0 -translate-y-1/2 rounded-md p-1 opacity-70 hover:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-background"
        aria-label={t("LAUNCH_BAR.DISMISS_ARIA", "Dismiss announcement")}
        data-testid="launch-announcement-bar-dismiss"
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}
